import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { getPortalOrder, uploadFile, getFiles, deleteFile } from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import { Button } from '../../components/ui/button';
import {
  ArrowLeft, Package, Car, Truck, MapPin, Clock, FileText,
  Upload, Trash, CheckCircle, Warning
} from '@phosphor-icons/react';
import { toast } from 'sonner';

const PortalOrderDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { user } = useAuth();
  const fileInputRef = useRef(null);
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);

  const loadOrder = useCallback(async () => {
    try {
      const res = await getPortalOrder(id);
      setOrder(res.data);
    } catch {
      toast.error('Failed to load order');
      navigate('/portal/orders');
    } finally {
      setLoading(false);
    }
  }, [id, navigate]);

  useEffect(() => { loadOrder(); }, [loadOrder]);

  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('order_id', id);
      formData.append('client_id', user?.id);
      formData.append('category', 'client_document');
      await uploadFile(formData);
      toast.success('Document uploaded');
      loadOrder();
    } catch (err) {
      toast.error('Failed to upload document');
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleDeleteFile = async (fileId) => {
    if (!window.confirm('Delete this document?')) return;
    try {
      await deleteFile(fileId);
      toast.success('Document deleted');
      loadOrder();
    } catch (err) {
      toast.error('Failed to delete document');
    }
  };

  const statusLabels = {
    new_lead: t('status.new_lead'),
    waiting_documents: t('status.waiting_documents'),
    documents_received: t('status.documents_received'),
    car_sourcing: t('status.car_sourcing'),
    vehicle_confirmed: t('status.vehicle_confirmed'),
    payment_pending: t('status.payment_pending'),
    payment_received: t('status.payment_received'),
    shipping_booked: t('status.shipping_booked'),
    in_transit: t('status.in_transit'),
    container_loaded: t('status.container_loaded'),
    bl_issued: t('status.bl_issued'),
    arrived_algeria: t('status.arrived_algeria'),
    customs_clearance: t('status.customs_clearance'),
    ready_delivery: t('status.ready_delivery'),
    delivered: t('status.delivered'),
    cancelled: t('status.cancelled')
  };

  const statusProgress = [
    'new_lead', 'waiting_documents', 'documents_received', 'car_sourcing',
    'vehicle_confirmed', 'payment_pending', 'payment_received', 'shipping_booked',
    'in_transit', 'container_loaded', 'bl_issued', 'arrived_algeria',
    'customs_clearance', 'ready_delivery', 'delivered'
  ];

  if (loading) {
    return (
      <div className="flex justify-center py-12">
        <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  if (!order) return null;

  const currentStatusIndex = statusProgress.indexOf(order.status);
  const showDocumentUpload = ['new_lead', 'waiting_documents'].includes(order.status);

  return (
    <div data-testid="portal-order-detail">
      {/* Header */}
      <div className="flex items-center gap-4 mb-6">
        <Button variant="ghost" size="icon" onClick={() => navigate('/portal/orders')} data-testid="back-to-orders">
          <ArrowLeft className="w-5 h-5" />
        </Button>
        <div className="flex-1">
          <div className="flex items-center gap-3">
            <Package weight="duotone" className="w-6 h-6 text-primary" />
            <h1 className="text-2xl font-bold tracking-tight font-mono">{order.order_number}</h1>
          </div>
        </div>
        <span className={`status-badge status-${order.status}`}>
          {statusLabels[order.status]}
        </span>
      </div>

      {/* Status Progress */}
      {order.status !== 'cancelled' && (
        <div className="card-brutalist mb-6">
          <h3 className="font-bold mb-4">Order Progress</h3>
          <div className="relative">
            <div className="flex justify-between">
              {['Order Placed', 'Documents', 'Vehicle', 'Shipping', 'Delivered'].map((step, idx) => {
                const stepPositions = [0, 2, 4, 7, 14];
                const isComplete = currentStatusIndex >= stepPositions[idx];
                const isCurrent = currentStatusIndex >= stepPositions[idx] && 
                  (idx === 4 || currentStatusIndex < stepPositions[idx + 1]);
                return (
                  <div key={step} className="flex flex-col items-center z-10">
                    <div className={`w-8 h-8 flex items-center justify-center ${
                      isComplete ? 'bg-green-600 text-white' : 'bg-secondary text-muted-foreground'
                    }`}>
                      {isComplete ? <CheckCircle weight="fill" className="w-5 h-5" /> : (idx + 1)}
                    </div>
                    <span className={`text-xs mt-2 text-center ${isCurrent ? 'font-bold text-primary' : 'text-muted-foreground'}`}>
                      {step}
                    </span>
                  </div>
                );
              })}
            </div>
            <div className="absolute top-4 left-0 right-0 h-0.5 bg-secondary -z-0">
              <div 
                className="h-full bg-green-600 transition-all" 
                style={{ width: `${Math.min(100, (currentStatusIndex / 14) * 100)}%` }}
              />
            </div>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Info */}
        <div className="lg:col-span-2 space-y-6">
          {/* Vehicle Info */}
          {order.vehicle && (
            <div className="card-brutalist">
              <div className="flex items-center gap-2 mb-4">
                <Car weight="duotone" className="w-5 h-5 text-muted-foreground" />
                <h3 className="font-bold">Vehicle Information</h3>
              </div>
              <div className="flex items-start gap-6">
                <div className="w-32 h-24 bg-secondary flex items-center justify-center flex-shrink-0">
                  {order.vehicle.photos?.[0] ? (
                    <img src={order.vehicle.photos[0]} alt="Vehicle" className="w-full h-full object-cover" />
                  ) : (
                    <Car weight="duotone" className="w-10 h-10 text-muted-foreground" />
                  )}
                </div>
                <div>
                  <p className="text-xl font-bold">{order.vehicle.brand_name} {order.vehicle.model_name}</p>
                  <p className="text-muted-foreground">{order.vehicle.year} • {order.vehicle.version}</p>
                  <div className="flex gap-4 mt-2 text-sm">
                    {order.vehicle.color && <span>Color: {order.vehicle.color}</span>}
                    {order.vehicle.fuel_type && <span>Fuel: {order.vehicle.fuel_type}</span>}
                    {order.vehicle.transmission && <span>{order.vehicle.transmission}</span>}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Shipping Info */}
          <div className="card-brutalist">
            <div className="flex items-center gap-2 mb-4">
              <Truck weight="duotone" className="w-5 h-5 text-muted-foreground" />
              <h3 className="font-bold">Shipping Information</h3>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
              <div>
                <p className="overline">Container #</p>
                <p className="font-mono font-bold">{order.container_number || '-'}</p>
              </div>
              <div>
                <p className="overline">Bill of Lading</p>
                <p className="font-mono font-bold">{order.bl_number || '-'}</p>
              </div>
              <div>
                <p className="overline">Shipping Company</p>
                <p className="font-bold">{order.shipping_company?.name || '-'}</p>
              </div>
              <div>
                <p className="overline">Departure Date</p>
                <p>{order.departure_date || 'TBD'}</p>
              </div>
              <div>
                <p className="overline">Estimated Arrival</p>
                <p className="font-bold">{order.estimated_arrival || 'TBD'}</p>
              </div>
              <div>
                <p className="overline">Current Location</p>
                <p>{order.current_location || 'Unknown'}</p>
              </div>
            </div>
          </div>

          {/* Documents */}
          <div className="card-brutalist">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <FileText weight="duotone" className="w-5 h-5 text-muted-foreground" />
                <h3 className="font-bold">Documents</h3>
              </div>
              {showDocumentUpload && (
                <div>
                  <input
                    type="file"
                    ref={fileInputRef}
                    onChange={handleFileUpload}
                    className="hidden"
                    accept=".pdf,.jpg,.jpeg,.png"
                  />
                  <Button
                    onClick={() => fileInputRef.current?.click()}
                    disabled={uploading}
                    className="btn-primary"
                    data-testid="upload-order-document-btn"
                  >
                    <Upload className="w-4 h-4 mr-2" />
                    {uploading ? 'Uploading...' : 'Upload Document'}
                  </Button>
                </div>
              )}
            </div>

            {showDocumentUpload && (
              <div className="bg-amber-50 border border-amber-200 p-4 mb-4">
                <div className="flex items-start gap-3">
                  <Warning weight="fill" className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="font-medium text-amber-800">Documents Required</p>
                    <p className="text-sm text-amber-700">
                      Please upload your passport/ID copy and any other required documents to proceed with your order.
                    </p>
                  </div>
                </div>
              </div>
            )}

            {order.documents?.length > 0 ? (
              <div className="space-y-2">
                {order.documents.map((doc) => (
                  <div key={doc.id} className="flex items-center justify-between p-3 border border-border">
                    <div className="flex items-center gap-3">
                      <FileText weight="duotone" className="w-5 h-5 text-muted-foreground" />
                      <div>
                        <p className="font-medium text-sm">{doc.original_filename}</p>
                        <p className="text-xs text-muted-foreground">{doc.category} • {(doc.size / 1024).toFixed(1)} KB</p>
                      </div>
                    </div>
                    {doc.uploaded_by === user?.id && (
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => handleDeleteFile(doc.id)}
                        className="text-destructive"
                      >
                        <Trash className="w-4 h-4" />
                      </Button>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-6 text-muted-foreground border border-dashed border-border">
                No documents available
              </div>
            )}
          </div>
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Status Timeline */}
          <div className="card-brutalist">
            <h3 className="font-bold mb-4">Status History</h3>
            {order.status_history?.length > 0 ? (
              <div className="space-y-4">
                {order.status_history.slice(0, 5).map((entry, idx) => (
                  <div key={`status-${entry.status}-${entry.timestamp || idx}`} className="flex items-start gap-3">
                    <div className={`w-6 h-6 flex items-center justify-center flex-shrink-0 ${
                      entry.status === 'delivered' ? 'bg-green-100' :
                      entry.status === 'cancelled' ? 'bg-red-100' : 'bg-secondary'
                    }`}>
                      {entry.status === 'delivered' ? (
                        <CheckCircle weight="fill" className="w-4 h-4 text-green-600" />
                      ) : entry.status === 'cancelled' ? (
                        <Warning weight="fill" className="w-4 h-4 text-red-600" />
                      ) : (
                        <Clock weight="duotone" className="w-3 h-3" />
                      )}
                    </div>
                    <div>
                      <span className={`status-badge status-${entry.status} text-xs`}>
                        {statusLabels[entry.status]}
                      </span>
                      <p className="text-xs text-muted-foreground mt-1">
                        {new Date(entry.timestamp).toLocaleDateString()}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-sm text-muted-foreground">No history available</p>
            )}
          </div>

          {/* Contact */}
          <div className="card-brutalist">
            <h3 className="font-bold mb-4">Need Help?</h3>
            <p className="text-sm text-muted-foreground mb-4">
              If you have questions about your order, please contact our support team.
            </p>
            <Button variant="outline" className="w-full">
              Contact Support
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PortalOrderDetail;
